import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { fmtMoney, formatRelative } from "../OrgOverview/format";
import {
  compareGoldenVersions,
  fetchGoldenVersionSnapshot,
  fetchGoldenVersions,
} from "./api";

const WAGE_TYPES = ["Base", "Bonus", "Overtime", "Commission"];

function money(value) {
  return typeof value === "number" ? fmtMoney(value) : "—";
}

function MoneyPair({ available, qualifying }) {
  return (
    <div className="icr-golden-money-pair">
      <span>
        <em>Available</em> {money(available)}
      </span>
      <span>
        <em>Qualifying</em> {money(qualifying)}
      </span>
    </div>
  );
}

function SnapshotTree({ snapshot }) {
  if (!snapshot?.borrowers?.length) {
    return (
      <p className="icr-muted">
        No borrower / employment breakdown for this version yet.
      </p>
    );
  }

  return (
    <div className="icr-golden-tree">
      <div className="icr-golden-tree__loan">
        <strong>Loan totals</strong>
        <MoneyPair
          available={snapshot.loanTotals?.available}
          qualifying={snapshot.loanTotals?.qualifying}
        />
      </div>
      {snapshot.borrowers.map((borrower, index) => (
        <section key={borrower.borrowerId} className="icr-golden-tree__br">
          <header>
            <h4>
              BR{index + 1}: {borrower.borrowerName || borrower.borrowerId}
            </h4>
            <MoneyPair
              available={borrower.availableTotal}
              qualifying={borrower.qualifyingTotal}
            />
          </header>
          {(borrower.employments || []).map((employment, empIndex) => (
            <div key={employment.employmentId} className="icr-golden-tree__emp">
              <div className="icr-golden-tree__emp-head">
                <strong>
                  EMP{empIndex + 1}:{" "}
                  {employment.employerName || employment.employmentId}
                </strong>
                <MoneyPair
                  available={employment.availableTotal}
                  qualifying={employment.qualifyingTotal}
                />
              </div>
              <table className="icr-golden-components">
                <thead>
                  <tr>
                    <th>Component</th>
                    <th className="icr-num">Available</th>
                    <th className="icr-num">Qualifying</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {WAGE_TYPES.map((type) => {
                    const component = employment.components?.[type];
                    if (
                      component?.available == null &&
                      component?.qualifying == null &&
                      !component?.status
                    ) {
                      return null;
                    }

                    return (
                      <tr key={type}>
                        <td>{type}</td>
                        <td className="icr-num">{money(component?.available)}</td>
                        <td className="icr-num">{money(component?.qualifying)}</td>
                        <td>{component?.status || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
          {(borrower.businesses || []).map((business, businessIndex) => (
            <div key={business.businessId} className="icr-golden-tree__emp">
              <div className="icr-golden-tree__emp-head">
                <strong>
                  SE{businessIndex + 1}:{" "}
                  {business.businessName || business.businessId}
                </strong>
                <MoneyPair
                  available={business.availableTotal}
                  qualifying={business.qualifyingTotal}
                />
              </div>
              <table className="icr-golden-components">
                <tbody>
                  <tr>
                    <td>Self-Employment</td>
                    <td className="icr-num">
                      {money(business.components?.SelfEmployment?.available)}
                    </td>
                    <td className="icr-num">
                      {money(business.components?.SelfEmployment?.qualifying)}
                    </td>
                    <td>{business.components?.SelfEmployment?.status || "—"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

function describeChange(cell) {
  const left = cell?.left;
  const right = cell?.right;
  const hasLeft = typeof left === "number";
  const hasRight = typeof right === "number";
  if (!hasLeft && !hasRight) {
    return { label: "—", tone: "empty", changed: false };
  }
  if (!hasLeft) {
    return { label: "Only on right", tone: "added", changed: true };
  }
  if (!hasRight) {
    return { label: "Only on left", tone: "removed", changed: true };
  }
  if (left === right) {
    return { label: "Same", tone: "same", changed: false };
  }
  const delta = Math.round((right - left) * 100) / 100;

  return {
    label: `${delta > 0 ? "+" : ""}${fmtMoney(delta)}`,
    tone: delta > 0 ? "up" : "down",
    changed: true,
  };
}

function flattenComparison(comparison) {
  const rows = [];
  const add = (section, line, cell) => {
    if (
      typeof cell?.left !== "number" &&
      typeof cell?.right !== "number"
    ) {
      return;
    }
    rows.push({
      key: `${section}/${line}`,
      section,
      line,
      cell,
      ...describeChange(cell),
    });
  };

  add("Loan totals", "Available", comparison?.loanTotals?.available);
  add("Loan totals", "Qualifying", comparison?.loanTotals?.qualifying);
  (comparison?.borrowers || []).forEach((borrower, borrowerIndex) => {
    const borrowerName =
      borrower.borrowerName ||
      borrower.name ||
      borrower.borrowerId ||
      `Borrower ${borrowerIndex + 1}`;
    add(borrowerName, "Available", borrower.availableTotal);
    add(borrowerName, "Qualifying", borrower.qualifyingTotal);
    (borrower.employments || []).forEach((employment, employmentIndex) => {
      const employmentName =
        employment.name ||
        employment.employerName ||
        employment.employmentId ||
        `Employment ${employmentIndex + 1}`;
      WAGE_TYPES.forEach((type) => {
        add(
          `${borrowerName} / ${employmentName}`,
          `${type} · Available`,
          employment.components?.[type]?.available,
        );
        add(
          `${borrowerName} / ${employmentName}`,
          `${type} · Qualifying`,
          employment.components?.[type]?.qualifying,
        );
      });
    });
    (borrower.businesses || []).forEach((business, businessIndex) => {
      const businessName =
        business.name ||
        business.businessName ||
        business.businessId ||
        `Business ${businessIndex + 1}`;
      add(
        `${borrowerName} / ${businessName}`,
        "Self-Employment · Available",
        business.components?.SelfEmployment?.available,
      );
      add(
        `${borrowerName} / ${businessName}`,
        "Self-Employment · Qualifying",
        business.components?.SelfEmployment?.qualifying,
      );
    });
  });

  return rows;
}

function CompareTree({ comparison, leftLabel, rightLabel }) {
  const [showUnchanged, setShowUnchanged] = useState(true);
  const rows = useMemo(() => flattenComparison(comparison), [comparison]);
  const changedCount = rows.filter((row) => row.changed).length;
  const visibleRows = showUnchanged
    ? rows
    : rows.filter((row) => row.changed);

  if (!comparison) {
    return <p className="icr-muted">No comparison returned.</p>;
  }

  return (
    <div className="icr-compare-tree">
      <div className="icr-compare-toolbar">
        <div className="icr-compare-legend">
          <span className="icr-change-chip icr-change-chip--same">Same</span>
          <span className="icr-change-chip icr-change-chip--up">+$…</span>
          <span className="icr-change-chip icr-change-chip--down">−$…</span>
          <span className="icr-change-chip icr-change-chip--added">Only on right</span>
          <span className="icr-change-chip icr-change-chip--removed">Only on left</span>
        </div>
        <label className="icr-compare-toggle">
          <input
            type="checkbox"
            checked={!showUnchanged}
            onChange={(event) => setShowUnchanged(!event.target.checked)}
          />
          Show differences only
          <small>{changedCount} differences</small>
        </label>
      </div>
      <div className="icr-table-scroll">
        <table className="icr-component-compare">
          <thead>
            <tr>
              <th>Section</th>
              <th>Line</th>
              <th className="icr-num">Left · {leftLabel}</th>
              <th className="icr-num">Right · {rightLabel}</th>
              <th>Change</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr key={row.key}>
                <td>{row.section}</td>
                <td>{row.line}</td>
                <td className="icr-num">{money(row.cell.left)}</td>
                <td className="icr-num">{money(row.cell.right)}</td>
                <td>
                  <span className={`icr-change-chip icr-change-chip--${row.tone}`}>
                    {row.label}
                  </span>
                </td>
              </tr>
            ))}
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={5} className="icr-muted">
                  No differences in this comparison.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function versionLabel(version) {
  if (!version) {
    return "Version";
  }

  return `${formatRelative(version.createdAt)} · ${version.source || version.status}`;
}

export default function GoldenVersionHistory({ goldenLoan, env }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leftJobId, setLeftJobId] = useState("");
  const [rightJobId, setRightJobId] = useState("");
  const [mode, setMode] = useState("single");
  const [snapshot, setSnapshot] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchGoldenVersions(goldenLoan.id, env);
      if (!result.success) {
        throw new Error(result.reason || "Failed to load versions.");
      }
      const next = Array.isArray(result.versions) ? result.versions : [];
      setVersions(next);
      setLeftJobId(next[0]?.jobId || "");
      setRightJobId(next[1]?.jobId || next[0]?.jobId || "");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [env, goldenLoan.id]);

  useEffect(() => {
    setSnapshot(null);
    setComparison(null);
    setMode("single");
    loadVersions();
  }, [loadVersions]);

  useEffect(() => {
    if (!leftJobId || (mode === "compare" && !rightJobId)) {
      return undefined;
    }
    let active = true;
    const load = async () => {
      setDetailLoading(true);
      try {
        const result =
          mode === "single"
            ? await fetchGoldenVersionSnapshot(goldenLoan.id, leftJobId, env)
            : await compareGoldenVersions(
                goldenLoan.id,
                { leftJobId, rightJobId },
                env,
              );
        if (!result.success) {
          throw new Error(result.reason || "Failed to load version detail.");
        }
        if (active) {
          setSnapshot(mode === "single" ? result.snapshot : null);
          setComparison(mode === "compare" ? result.comparison : null);
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        if (active) {
          setDetailLoading(false);
        }
      }
    };
    load();

    return () => {
      active = false;
    };
  }, [env, goldenLoan.id, leftJobId, mode, rightJobId]);

  return (
    <section className="icr-golden-history">
      <div className="icr-golden-history__header">
        <div>
          <h3>Version history</h3>
          <p>
            Preserve the existing run tree and two-version comparison for this
            selected golden loan.
          </p>
        </div>
        <button className="icr-btn" type="button" onClick={loadVersions}>
          Refresh versions
        </button>
      </div>
      {loading ? (
        <p className="icr-muted">Loading versions…</p>
      ) : versions.length === 0 ? (
        <p className="icr-muted">No income runs yet for this loan.</p>
      ) : (
        <>
          <div className="icr-golden-version-controls">
            <div className="icr-mode-switch" role="tablist" aria-label="History mode">
              <button
                type="button"
                role="tab"
                aria-selected={mode === "single"}
                className={mode === "single" ? "is-active" : ""}
                onClick={() => setMode("single")}
              >
                Single version
                <small>Inspect one run tree</small>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === "compare"}
                className={mode === "compare" ? "is-active" : ""}
                onClick={() => setMode("compare")}
              >
                Compare versions
                <small>Readable Left / Right / Change</small>
              </button>
            </div>
            <label>
              {mode === "compare" ? "Left version" : "Version"}
              <select
                className="icr-select"
                value={leftJobId}
                onChange={(event) => setLeftJobId(event.target.value)}
              >
                {versions.map((version) => (
                  <option key={version.jobId} value={version.jobId}>
                    {formatRelative(version.createdAt)} · {version.status} · Q{" "}
                    {money(version.qualifyingMonthlyIncome)}
                  </option>
                ))}
              </select>
            </label>
            {mode === "compare" && (
              <label>
                Right version
                <select
                  className="icr-select"
                  value={rightJobId}
                  onChange={(event) => setRightJobId(event.target.value)}
                >
                  {versions.map((version) => (
                    <option key={version.jobId} value={version.jobId}>
                      {formatRelative(version.createdAt)} · {version.status} · Q{" "}
                      {money(version.qualifyingMonthlyIncome)}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {detailLoading ? (
            <p className="icr-muted">Loading breakdown…</p>
          ) : mode === "compare" ? (
            <CompareTree
              comparison={comparison}
              leftLabel={versionLabel(
                versions.find((version) => version.jobId === leftJobId),
              )}
              rightLabel={versionLabel(
                versions.find((version) => version.jobId === rightJobId),
              )}
            />
          ) : (
            <SnapshotTree snapshot={snapshot} />
          )}
        </>
      )}
    </section>
  );
}
